'use client'

import axios from 'axios'
import { createContext, useCallback, useEffect, useState } from 'react'

export const AuthContext = createContext({
  userToken: null,
  setUserToken: () => {},
  refreshToken: null,
  setRefreshToken: () => {},
  userData: null,
  setUserData: () => {},
  logout: () => {},
  refreshAccessToken: () => {},
  isAuthenticated: false,
})

export function AuthProvider({ children }) {
  const [userToken, setUserToken] = useState(null)
  const [refreshToken, setRefreshToken] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isChecking, setIsChecking] = useState(true)

  // Initialize from localStorage on mount and check authentication
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('userToken')
      const storedRefreshToken = localStorage.getItem('refreshToken')
      const storedUserData = localStorage.getItem('userData')

      if (storedToken) {
        setUserToken(storedToken)
      } else {
        // No token found, redirect to login only if not already on a public route
        const currentPath = window.location.pathname
        const publicRoutes = [
          '/login',
          '/authentication',
          '/signup-with-invitation',
          '/accept-space-join-invitation',
          '/invitation-expired',
          '/invitation-accepted',
          '/privacy',
          '/terms',
          '/session-expired',
          '/manifest.webmanifest',
          '/manifest.json',
          '/sw.js',
          '/user-access',
        ]
        const isPublicRoute = publicRoutes.some((route) =>
          currentPath.startsWith(route)
        )

        if (!isPublicRoute) {
          window.location.href = '/login'
          return
        }
      }

      if (storedRefreshToken) {
        setRefreshToken(storedRefreshToken)
      }

      if (storedUserData) {
        try {
          setUserData(JSON.parse(storedUserData))
        } catch (e) {
          console.error('Failed to parse user data:', e)
        }
      }

      setIsChecking(false)
    }
  }, [])

  // Logout function
  const logout = useCallback(() => {
    // Clear all session data
    localStorage.removeItem('userToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userData')
    localStorage.removeItem('lastActivityTime')
    localStorage.removeItem('ababilLogin')

    // Clear cookies
    document.cookie =
      'userToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    document.cookie =
      'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'

    // Reset state
    setUserToken(null)
    setRefreshToken(null)
    setUserData(null)

    // Redirect to login
    window.location.href = '/login'
  }, [])

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem('refreshToken')

    if (!storedRefreshToken) {
      logout()
      return null
    }

    try {
      const baseUrl =
        process.env.NEXT_PUBLIC_PROJECT_ENV === 'prod'
          ? process.env.NEXT_PUBLIC_API_URL_AUTH_PROD
          : process.env.NEXT_PUBLIC_API_URL_AUTH

      const response = await axios.post(
        `${baseUrl}/usersV1/public/refresh-token`,
        {
          refreshToken: storedRefreshToken,
        }
      )

      if (response?.data?.error === false) {
        const newAccessToken = response?.data?.body?.data?.accessToken
        const newRefreshToken = response?.data?.body?.data?.refreshToken

        // Update tokens
        localStorage.setItem('userToken', newAccessToken)
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken)
          setRefreshToken(newRefreshToken)
        }
        setUserToken(newAccessToken)

        // Update cookies
        document.cookie = `userToken=${newAccessToken}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`
        if (newRefreshToken) {
          document.cookie = `refreshToken=${newRefreshToken}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
        }

        return newAccessToken
      } else {
        logout()
        return null
      }
    } catch (error) {
      console.error('Failed to refresh token:', error)
      logout()
      return null
    }
  }, [logout])

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('userToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const newToken = await refreshAccessToken()
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return axios(originalRequest)
            }
          } catch (refreshError) {
            logout()
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.request.eject(requestInterceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, [refreshAccessToken, logout])

  // Show nothing while checking authentication
  if (isChecking) {
    return null
  }

  // Calculate authentication state
  const isAuthenticated = !!userToken

  return (
    <AuthContext.Provider
      value={{
        userToken,
        setUserToken,
        refreshToken,
        setRefreshToken,
        userData,
        setUserData,
        logout,
        refreshAccessToken,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
